import { useState, useEffect } from 'react';
import { UtensilsCrossed, Calendar, ShoppingCart, Apple, ChefHat, BarChart3, Scale, Activity, Heart, ClipboardList, BookOpen, X } from 'lucide-react';

// ── Wizard imports ──

import PlanGeneratorSheet from './PlanGeneratorSheet';
import WeightWizard from './wizards/WeightWizard';
import ActivityWizard from './wizards/ActivityWizard';
import ReviewWizard from './wizards/ReviewWizard';
import PreferencesWizard from './wizards/PreferencesWizard';
import PlanningRulesWizard from './wizards/PlanningRulesWizard';
import LogMealModal from '../pages/LogMealModal';
import MealEstimateWizard from './wizards/MealEstimateWizard';
import ShoppingWizard from './wizards/ShoppingWizard';
import SnackWizard from './wizards/SnackWizard';
import RecipeCreateWizard from './wizards/RecipeCreateWizard';

// ── Types ──

export interface ActionCard {
  id: string;
  label: string;
  icon: string;
  description: string;
  needsAI?: boolean;
}

const ACTION_CARDS: ActionCard[] = [
  { id: 'estimate_meal', label: 'Essen tracken', icon: '🍽️', description: 'Freitext oder Foto', needsAI: true },
  { id: 'log_meal', label: 'Bekanntes loggen', icon: '📖', description: 'Rezept oder Produkt wählen' },
  { id: 'generate_plan', label: 'Wochenplan', icon: '📅', description: 'Plan für nächste Woche', needsAI: true },
  { id: 'shopping', label: 'Einkaufsliste', icon: '🛒', description: 'Liste erstellen & normalisieren', needsAI: true },
  { id: 'snack', label: 'Snack-Idee', icon: '🍎', description: 'Vorschlag nach Restbudget', needsAI: true },
  { id: 'new_recipe', label: 'Neues Rezept', icon: '👩‍🍳', description: 'Manuell anlegen oder generieren' },
  { id: 'review', label: 'Wochen-Review', icon: '📊', description: 'Woche auswerten' },
  { id: 'weight', label: 'Gewicht', icon: '⚖️', description: 'Gewicht eintragen' },
  { id: 'activity', label: 'Aktivität', icon: '🏃', description: 'Bewegung loggen' },
  { id: 'preferences', label: 'Präferenzen', icon: '❤️', description: 'Vorlieben & Allergien' },
  { id: 'rules', label: 'Planungsregeln', icon: '📋', description: 'Regeln für Wochenpläne' },
];

// ── Component ──

// Expose open state so Nico FAB can trigger it
let globalOpenHub: (() => void) | null = null;
export function openActionHub() { globalOpenHub?.(); }

// Expose plan sheet opener for PlanJobBanner
let globalOpenPlanSheet: (() => void) | null = null;
export function openPlanSheet() { globalOpenPlanSheet?.(); }

export default function FloraActionHub() {
  const [open, setOpen] = useState(false);
  const [wiggle, setWiggle] = useState(false);

  // Register global opener
  useEffect(() => {
    globalOpenHub = () => {
      setActiveWizard(null); // Close any open wizard first
      setOpen(true);
    };
    globalOpenPlanSheet = () => {
      setOpen(false);
      setActiveWizard('generate_plan');
    };
    return () => { globalOpenHub = null; globalOpenPlanSheet = null; };
  }, []);

  // Active wizard state — only one wizard open at a time
  const [activeWizard, setActiveWizard] = useState<string | null>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleToggle = () => {
    setWiggle(true);
    setTimeout(() => setWiggle(false), 600);
    setOpen(prev => !prev);
  };

  const handleCardClick = (card: ActionCard) => {
    setOpen(false);
    // Small delay to let the hub close before opening wizard
    setTimeout(() => setActiveWizard(card.id), 150);
  };

  const closeWizard = () => setActiveWizard(null);

  return (
    <>
      {/* Action Hub panel — only show when no wizard is active */}
      {open && !activeWizard && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onMouseDown={() => setOpen(false)} />

          {/* Bottom sheet (mobile) / Side panel (desktop) */}
          <div className="fixed bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto md:w-[420px] z-50 flora-menu-appear">
            <div className="bg-gray-50 md:rounded-2xl md:shadow-2xl md:border md:border-gray-200 flex flex-col overflow-hidden"
                 style={{ maxHeight: 'min(80vh, 640px)' }}>

              {/* Header */}
              <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-teal-600 to-teal-400 text-white shrink-0">
                <div className="flex items-center gap-3">
                  <img src="/nico-transparent.svg" alt="Nico" className="w-16 h-16 -my-3" />
                  <div>
                    <span className="font-display font-semibold text-sm">plate</span>
                    <p className="text-[11px] text-white/70 -mt-0.5">Was möchtest du tun?</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                  <X size={16} strokeWidth={2.5} />
                </button>
              </div>

              {/* Action Grid */}
              <div className="overflow-y-auto px-4 pb-4 flex-1">
                <div className="grid grid-cols-2 gap-2">
                  {ACTION_CARDS.map(card => (
                    <button
                      key={card.id}
                      onClick={() => handleCardClick(card)}
                      className="flex flex-col items-start gap-1 p-3 bg-white rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50 transition-all text-left active:scale-[0.97]"
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="text-lg">{card.icon}</span>
                        <span className="text-sm font-medium text-gray-800 truncate">{card.label}</span>
                      </div>
                      <span className="text-[11px] text-gray-400 leading-tight">{card.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Safe area padding */}
              <div className="shrink-0" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
            </div>
          </div>
        </>
      )}

      {/* FAB is now handled by Nico component on Dashboard, or global openActionHub() */}

      {/* ── Wizards ── */}

      {/* Plan Generator */}
      <PlanGeneratorSheet
        open={activeWizard === 'generate_plan'}
        onClose={closeWizard}
      />

      {/* Phase 1: Simple wizards (no Claude) */}
      <WeightWizard open={activeWizard === 'weight'} onClose={closeWizard} />
      <ActivityWizard open={activeWizard === 'activity'} onClose={closeWizard} />
      <ReviewWizard open={activeWizard === 'review'} onClose={closeWizard} />
      <PreferencesWizard open={activeWizard === 'preferences'} onClose={closeWizard} />
      <PlanningRulesWizard open={activeWizard === 'rules'} onClose={closeWizard} />
      <LogMealModal open={activeWizard === 'log_meal'} onClose={closeWizard} onSaved={closeWizard} />
      <MealEstimateWizard open={activeWizard === 'estimate_meal'} onClose={closeWizard} />
      <ShoppingWizard open={activeWizard === 'shopping'} onClose={closeWizard} />
      <SnackWizard open={activeWizard === 'snack'} onClose={closeWizard} />
      <RecipeCreateWizard open={activeWizard === 'new_recipe'} onClose={closeWizard} />
    </>
  );
}
